import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import CustomDropdown from 'components/Dropdown';
import SectorFilter from 'components/Dropdown/SectorFilter';
import ReactDateRangeFilter from 'components/Dropdown/ReactDateRangeFilter';
import LoaderComponent from 'components/Loader';
import { workspaceGetEnv } from 'service/service';
import type {
  FilterConfigSection,
  FilterValues,
} from 'components/Dropdown/MultiFilterPanel/type';
import MultiFilterPanel from 'components/Dropdown/MultiFilterPanel';
import { useHeader } from 'Context/contexts/HeaderTabRightContentContext';

type Option = { label: string; value: string };

interface Props {
  location: string;
  setLocation: React.Dispatch<React.SetStateAction<string>>;
  env: string;
  setEnv: React.Dispatch<React.SetStateAction<string>>;
  envOptions?: Option[];
  // accept same setter signature as useState in workspace
  setEnvOptions?: React.Dispatch<React.SetStateAction<Option[]>>;
  startDate: string;
  endDate: string;
  setStartDate: React.Dispatch<React.SetStateAction<string>>;
  setEndDate: React.Dispatch<React.SetStateAction<string>>;
  handleDateSelect: (
    st: string,
    ed: string,
    selected?: string,
    is24HrsTime?: boolean
  ) => void;
  selectedDateInString: string;
  setSelectedDateInString?: React.Dispatch<React.SetStateAction<string>>;
  sectorName: string;
  setSectorName: React.Dispatch<React.SetStateAction<string>>;
  sectorOptionsList?: string[];
  updateHistoryState?: (overrides: Partial<{
    startDate: string;
    endDate: string;
    env: string;
    sector: string;
    location: string;
    selectedDateInString: string;
    filters?: any;
    contentName?: string;
    workspaceName?: string;
    warehouseName?: string;
    warehouseType?: string;
    userExecutedBy?: string;
    catalogName?: string;
  }>) => void;
  hasYTD?: boolean;
  searchSource?: string;

  // UI revamp flag only.
  // false -> existing inline filters
  // true  -> date + MultiFilterPanel in TabPageHeader right content
  useHeaderFilters?: boolean;
}

const defaultRegions = [
  'ALL',
  'Brazil South',
  'Australia East',
  'UK South',
  'South Central US',
  'East US',
  'Germany West Central'
];

const regionOptions = defaultRegions.map(r => ({ label: r, value: r }));

const SQL_SOURCES = [
  'queryVolumeTrend',
  'totalCountByCategory',
  'totalCostByCategory',
  'warehouseCostTrend',
  'costSqlWarehouse',
  'warehouseByCategory',
  'dbuUsageSqlWarehouses',
  'userByDbuUsage',
  'userByCost',
  'catalogsCount',
  'catalogsCost'
];

const DEFAULT_SECTORS = [
  'latam',
  'apac',
  'TBD',
  'amesa',
  'essa',
  'flna',
  'global',
  'tbd',
  'pbna',
  'pfna',
  'snt'
];

const HeaderFilterContent = ({
  content,
}: {
  content: React.ReactNode;
}) => {
  const { setRightContextContent } = useHeader();

  useEffect(() => {
    setRightContextContent(content);

    return () => {
      setRightContextContent(null);
    };
  }, [content, setRightContextContent]);

  return null;
};

const FiltersBar: React.FC<Props> = ({
  location,
  setLocation,
  env,
  setEnv,
  envOptions,
  setEnvOptions,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  handleDateSelect,
  selectedDateInString,
  setSelectedDateInString,
  sectorName,
  setSectorName,
  sectorOptionsList = DEFAULT_SECTORS,
  updateHistoryState,
  hasYTD = false,
  searchSource,
  useHeaderFilters = false,
}) => {
  const [loaderEnv, setLoaderEnv] = useState(false);
  const initialEnvAppliedRef = useRef(false);
  const envRef = useRef(env);

  useEffect(() => {
    envRef.current = env;
  }, [env]);

  const fetchEnv = async (loc: string) => {
    try {
      setLoaderEnv(true);
      const res = await workspaceGetEnv(loc);
      const opts = [{ label: 'ALL', value: 'both' }, ...(res?.data || [])];

      if (setEnvOptions) {
        setEnvOptions(opts);
      }

      // preserve parent-provided env if that was provided via navigation
      if (!initialEnvAppliedRef.current && env) {
        initialEnvAppliedRef.current = true;
        return;
      }

      const available = opts.map(o => o.value);
      const cur = envRef.current;

      if (!available.includes(cur)) {
        updateEnvAndHistory(available);
      }
    } catch (e) {
      console.error('Failed to fetch env options', e);
    } finally {
      setLoaderEnv(false);
    }
  };

  const updateEnvAndHistory = (available: string[]) => {
    const fallback = available.includes('both') ? 'both' : (available[0] || 'both');
    setEnv(fallback);

    if (updateHistoryState) {
      const isSpecialSource = ['dbuUsageSqlWarehouses', 'costSqlWarehouse', 'computeSpend', 'runtime'].includes(searchSource || '');
      const isNotSqlSource = !SQL_SOURCES.includes(searchSource || '');

      updateHistoryState({
        env: fallback,
        filters: isSpecialSource ? [] : undefined,
        contentName: 'ALL',
        workspaceName: 'all',
        warehouseName: 'all',
        ...(isNotSqlSource && { warehouseType: 'all' }),
        userExecutedBy: 'all',
        catalogName: 'all'
      });
    }
  };

  useEffect(() => {
    // fetch env list whenever location changes
    fetchEnv(location);
  }, [location]);

  const onLocationChange = (evt: any) => {
    const newLoc = evt.target.value;
    setLocation(newLoc);
    setEnv('both'); // business requirement: reset to all on location change
    if (updateHistoryState) updateHistoryState({
      location: newLoc,
      env: 'both',
      filters: ['dbuUsageSqlWarehouses', 'costSqlWarehouse', 'computeSpend', 'runtime'].includes(searchSource || '') ? [] : undefined,
      contentName: 'ALL',
      workspaceName: 'all',
      warehouseName: 'all',
      ...(!SQL_SOURCES.includes(searchSource || '') && { warehouseType: 'all' }),
      userExecutedBy: 'all',
      catalogName: 'all'
    });
    initialEnvAppliedRef.current = true;
    fetchEnv(newLoc);
  };

  const onEnvChange = (evt: any) => {
    const selected = evt.target.value;
    setEnv(selected);
    if (updateHistoryState) updateHistoryState({
      env: selected,
      filters: ['dbuUsageSqlWarehouses', 'costSqlWarehouse', 'computeSpend', 'runtime'].includes(searchSource || '') ? [] : undefined,
      contentName: 'ALL',
      workspaceName: 'all',
      warehouseName: 'all',
      ...(!SQL_SOURCES.includes(searchSource || '') && { warehouseType: 'all' }),
      userExecutedBy: 'all',
      catalogName: 'all'
    });
  };

  const onSectorChange = (val: string) => {
    setSectorName(val);
    // business requirement: set env to both when sector changes
    setEnv('both');
    if (updateHistoryState) updateHistoryState({
      sector: val,
      env: 'both',
      filters: ['dbuUsageSqlWarehouses', 'costSqlWarehouse', 'computeSpend', 'runtime'].includes(searchSource || '') ? [] : undefined,
      contentName: 'ALL',
      workspaceName: 'all',
      warehouseName: 'all',
      ...(!SQL_SOURCES.includes(searchSource || '') && { warehouseType: 'all' }),
      userExecutedBy: 'all',
      catalogName: 'all'
    });
  };

  // wrapper to accept SetStateAction<string> (function or value) and call onSectorChange with plain string
  const handleSetSectorName: React.Dispatch<React.SetStateAction<string>> = (v) => {
    const resolved = typeof v === 'function'
      ? (v as (prev: string) => string)(sectorName)
      : v;

    onSectorChange(resolved);
  };

  /*
   * MultiFilterPanel is presentation only.
   * Parent/business values remain unchanged:
   * location ALL, environment both, sector all.
   */
  const currentPanelValues = useMemo<FilterValues>(() => ({
    location: location === 'ALL' ? null : location,
    env: env === 'both' ? null : env,
    sector: sectorName.toLowerCase() === 'all' ? null : sectorName,
  }), [location, env, sectorName]);

  const [appliedValues, setAppliedValues] =
    useState<FilterValues>(currentPanelValues);

  useEffect(() => {
    setAppliedValues(currentPanelValues);
  }, [currentPanelValues]);

  const locationFilterOptions = useMemo<Option[]>(
    () => regionOptions,
    []
  );

  /*
   * Environment options are exactly the options already populated by
   * the existing workspaceGetEnv flow.
   */
  const environmentFilterOptions = useMemo<Option[]>(
    () => envOptions ?? [],
    [envOptions]
  );

  const sectorFilterOptions = useMemo<Option[]>(
    () => sectorOptionsList.map(value => ({
      label: value,
      value,
    })),
    [sectorOptionsList]
  );

  const filterSections = useMemo<FilterConfigSection[]>(() => [
    {
      items: [
        {
          key: 'location',
          label: 'Region',
          type: 'dropdown_single',
          options: locationFilterOptions,
          placeholder: 'ALL',
          showSearch: true,
          showAllOption: false,
        },
        {
          key: 'env',
          label: 'Environment',
          type: 'dropdown_single',
          options: environmentFilterOptions,
          placeholder: 'ALL',
          showSearch: true,
          showAllOption: false,
        },
        {
          key: 'sector',
          label: 'Sector',
          type: 'dropdown_single',
          options: sectorFilterOptions,
          placeholder: 'ALL',
          showSearch: true,
          showAllOption: false,
        },
      ],
    },
  ], [
    environmentFilterOptions,
    locationFilterOptions,
    sectorFilterOptions,
  ]);

  /*
   * Use the existing handlers so business behavior is unchanged.
   * Location/Sector changes still reset Environment to both.
   */
  const handleHeaderFilterApply = (values: FilterValues) => {
    const nextLocation = values.location == null
      ? 'ALL'
      : String(values.location);

    const nextEnv = values.env == null
      ? 'both'
      : String(values.env);

    const nextSector = values.sector == null
      ? 'all'
      : String(values.sector);

    const locationChanged = nextLocation !== location;
    const sectorChanged = nextSector !== sectorName;
    const envChanged = nextEnv !== env;

    if (locationChanged) {
      onLocationChange({
        target: {
          value: nextLocation,
        },
      });
    }

    if (sectorChanged) {
      onSectorChange(nextSector);
    }

    if (!locationChanged && !sectorChanged && envChanged) {
      onEnvChange({
        target: {
          value: nextEnv,
        },
      });
    }

    setAppliedValues({
      location: nextLocation === 'ALL' ? null : nextLocation,
      env: locationChanged || sectorChanged || nextEnv === 'both'
        ? null
        : nextEnv,
      sector: nextSector.toLowerCase() === 'all'
        ? null
        : nextSector,
    });
  };

  const handleHeaderFilterClear = () => {
    const locationChanged = location !== 'ALL';
    const sectorChanged = sectorName.toLowerCase() !== 'all';

    if (locationChanged) {
      onLocationChange({
        target: {
          value: 'ALL',
        },
      });
    }

    if (sectorChanged) {
      onSectorChange('all');
    }

    if (!locationChanged && !sectorChanged && env !== 'both') {
      onEnvChange({
        target: {
          value: 'both',
        },
      });
    }

    setAppliedValues({
      location: null,
      env: null,
      sector: null,
    });
  };

  const headerContent = useMemo(() => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        height: 40,
      }}
    >
      <ReactDateRangeFilter
        startingDate={startDate}
        endingDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        handleDateSelect={handleDateSelect}
        isDbuSql={true}
        initialSelectedDate={selectedDateInString}
        hasYTD={hasYTD}
        isNew={useHeaderFilters}
      />

      <MultiFilterPanel
        sections={filterSections}
        appliedValues={appliedValues}
        setAppliedValues={setAppliedValues}
        onApplyFilters={handleHeaderFilterApply}
        onClear={handleHeaderFilterClear}
        disabled={loaderEnv}
      />
    </Box>
  ), [
    appliedValues,
    endDate,
    filterSections,
    hasYTD,
    loaderEnv,
    selectedDateInString,
    startDate,
  ]);

  if (useHeaderFilters) {
    return <HeaderFilterContent content={headerContent} />;
  }

  /*
   * Existing UI remains unchanged for all pages that do not use
   * useHeaderFilters.
   */
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <CustomDropdown
        handleChange={onLocationChange}
        value={location}
        options={regionOptions}
      />

      {loaderEnv ? (
        <LoaderComponent size={30} />
      ) : (
        <CustomDropdown
          handleChange={onEnvChange}
          value={env}
          options={envOptions ?? []}
        />
      )}

      <ReactDateRangeFilter
        startingDate={startDate}
        endingDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        handleDateSelect={handleDateSelect}
        isDbuSql={true}
        initialSelectedDate={selectedDateInString}
        hasYTD={hasYTD}
      />

      <SectorFilter
        sectorName={sectorName}
        setSectorName={handleSetSectorName}
        sectorOptionsList={sectorOptionsList}
        handleChange={handleSetSectorName}
      />
    </div>
  );
};

export default FiltersBar;
